import React, { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";

import HomePage from "scenes/home";
import ImpressumPage from "scenes/impressum";
import DatenschutzPage from "scenes/datenschutz";
import AgbsPage from "scenes/agbs";
import KontaktPage from "scenes/kontakt";
import KarrierePage from "scenes/karriere";

import LoginPage from "scenes/auth/LoginPage";
import TawanHandoffPage from "scenes/auth/TawanHandoffPage";
import ReferrerCapture from "components/ReferrerCapture";
import RegisterPage from "scenes/auth/RegisterPage";
import ForgotPasswordPage from "scenes/auth/ForgotPasswordPage";
import ResetPasswordPage from "scenes/auth/ResetPasswordPage";
import VerifyEmailPage from "scenes/auth/VerifyEmailPage";
import DeleteAccountConfirmPage from "scenes/auth/DeleteAccountConfirmPage";

import BookingPage from "scenes/booking";
import BookingSuccessPage from "scenes/booking/SuccessPage";

import KontoPage from "scenes/konto";
import ReschedulePage from "scenes/konto/ReschedulePage";

import QrRegisterPage from "scenes/qr/RegisterPage";
import TeamPage from "scenes/team";
import EmployeePage from "scenes/employee";
import StaffRejectBookingPage from "scenes/team/StaffRejectBookingPage";
import StaffBookingResultPage from "scenes/team/StaffBookingResultPage";

import RedirectWithSearch from "components/RedirectWithSearch";
import QrCodeGuestGate from "components/QrCodeGuestGate";
import AnalyticsPageView from "components/AnalyticsPageView";
import ScrollToTop from "utils/ScrollToTop";
import useAuthSync from "utils/useAuthSync";
import { getRouterBasename } from "utils/deploymentScope";
import { themeSettings } from "./theme";

import LeonardoNikolaiLanding from "./scenes/leonardoNikolai";
import { LeonardoLocaleProvider } from "./scenes/leonardoNikolai/LeonardoLocaleContext";
import LeonardoNyxLanding from "./scenes/leonardoNyx";
import { LeonardoLocaleProvider as LeonardoNyxLocaleProvider } from "./scenes/leonardoNyx/LeonardoLocaleContext";



const RequireAuth = ({ children }) => {
  const token = useSelector((s) => s.token);
  if (!token) return <Navigate to="/einloggen" replace />;
  return children;
};

const AppShell = () => {
  useAuthSync();
  return (
    <>
      <ReferrerCapture />
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/kontakt" element={<KontaktPage />} />
      <Route path="/karriere" element={<KarrierePage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/agbs" element={<AgbsPage />} />

      <Route path="/einloggen" element={<LoginPage />} />
      <Route path="/auth/tawan-weiterleitung" element={<TawanHandoffPage />} />
      <Route path="/registrieren" element={<RegisterPage />} />
      <Route path="/auth/verifizieren" element={<VerifyEmailPage />} />
      <Route path="/auth/passwort-vergessen" element={<ForgotPasswordPage />} />
      <Route path="/auth/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
      <Route path="/auth/konto-loeschen" element={<DeleteAccountConfirmPage />} />

      <Route path="/buchen" element={<BookingPage />} />
      <Route path="/buchen/erfolg" element={<BookingSuccessPage />} />

      <Route path="/konto" element={<RequireAuth><KontoPage /></RequireAuth>} />
      <Route path="/admin-dashboard" element={<TeamPage />} />
      <Route path="/mitarbeiter" element={<EmployeePage />} />
      <Route path="/team" element={<Navigate to="/admin-dashboard" replace />} />
      <Route path="/team/buchung/ablehnen" element={<StaffRejectBookingPage />} />
      <Route path="/team/buchung/ergebnis" element={<StaffBookingResultPage />} />
      <Route
        path="/konto/buchungen/:id/aendern"
        element={<RequireAuth><ReschedulePage /></RequireAuth>}
      />
      <Route path="/leonardo-nikolai" element={<LeonardoNikolaiLanding />} />
      <Route
        path="/leonardo-nyx"
        element={
          <LeonardoNyxLocaleProvider>
            <LeonardoNyxLanding />
          </LeonardoNyxLocaleProvider>
        }
      />

      <Route
        path="/qr-code"
        element={
          <QrCodeGuestGate>
            <QrRegisterPage />
          </QrCodeGuestGate>
        }
      />
      <Route path="/qr" element={<RedirectWithSearch to="/qr-code" />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  const theme = useMemo(() => createTheme(themeSettings("light")), []);
  return (
    <div className="app">
      <BrowserRouter basename={getRouterBasename()}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ScrollToTop />
          <AnalyticsPageView />
          <LeonardoLocaleProvider>
            <AppShell />
          </LeonardoLocaleProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
